import React, { useState } from 'react';
import {
    Box, Flex, Text, HStack, VStack, Badge, Button, Collapse, Icon, Divider, Tooltip, Wrap, WrapItem,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp, FaUndo, FaPaperPlane } from 'react-icons/fa';
import StatusComissao from '../StatusComissao';
import { formatarCentavos, formatarData, formatarDataHora } from '../formato';

// Um pagamento registrado, com os itens e o envio ao site.
const CartaoFechamento = ({ fechamento: f, podeGerenciar, onDesfazer, onDesfazerItem, onReenviar }) => {
    const [aberto, setAberto] = useState(false);
    const temPendenteSite = f.site.pendente > 0 || f.site.recusado > 0;

    return (
        <Box bg="white" borderWidth="1px" borderColor={f.desfeito ? 'gray.200' : 'green.200'} borderRadius="xl" overflow="hidden" opacity={f.desfeito ? 0.75 : 1}>
            <Flex px={{ base: 3, md: 4 }} py={3} gap={3} align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }}>
                <Box flex="1" minW={0}>
                    <HStack spacing={2} flexWrap="wrap">
                        <Text fontWeight="bold" color="gray.800">Pagamento #{f.id}</Text>
                        <Text fontSize="sm" color="gray.600">{formatarData(f.data_pagamento)} · {f.forma_pagamento_label}</Text>
                        {f.desfeito && <Badge colorScheme="red" textTransform="none" borderRadius="full" px={2}>Desfeito</Badge>}
                        {f.parcialmente_desfeito && <Badge colorScheme="orange" textTransform="none" borderRadius="full" px={2}>Parcialmente desfeito</Badge>}
                        {f.site.total > 0 && (
                            <StatusComissao
                                site
                                situacao={f.site.recusado > 0 ? 'recusado' : f.site.pendente > 0 ? 'erro' : 'ok'}
                                sufixo={`(${f.site.ok}/${f.site.total})`}
                            />
                        )}
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                        Registrado por {f.criado_por || 'Sistema'} em {formatarDataHora(f.criado_em)}
                        {f.desfeito && f.desfeito_por ? ` · desfeito por ${f.desfeito_por} em ${formatarDataHora(f.desfeito_em)}` : ''}
                    </Text>
                    {f.observacao && <Text fontSize="xs" color="gray.600" mt={1} noOfLines={2}>{f.observacao}</Text>}
                    {f.desfeito && f.motivo_desfazer && <Text fontSize="xs" color="red.600" mt={1}>Motivo: {f.motivo_desfazer}</Text>}
                </Box>

                <Flex align="center" gap={2} flexWrap="wrap" justify={{ base: 'space-between', md: 'flex-end' }} w={{ base: '100%', md: 'auto' }}>
                    <Box textAlign="right" mr={2}>
                        <Text fontSize="lg" fontWeight="bold" color={f.desfeito ? 'gray.500' : 'green.700'} textDecoration={f.desfeito ? 'line-through' : 'none'}>
                            {formatarCentavos(f.parcialmente_desfeito ? f.valor_ativo_centavos : f.valor_total_centavos)}
                        </Text>
                        {f.parcialmente_desfeito && <Text fontSize="10px" color="gray.500">de {formatarCentavos(f.valor_total_centavos)}</Text>}
                    </Box>
                    <HStack spacing={1}>
                        {podeGerenciar && temPendenteSite && !f.desfeito && (
                            <Tooltip label="Tentar de novo avisar o Sistema de Parceiros do site" hasArrow>
                                <Button size="xs" variant="outline" colorScheme="purple" leftIcon={<Icon as={FaPaperPlane} />} onClick={() => onReenviar(f)}>
                                    Reenviar ao site
                                </Button>
                            </Tooltip>
                        )}
                        {podeGerenciar && !f.desfeito && (
                            <Button size="xs" variant="outline" colorScheme="red" leftIcon={<Icon as={FaUndo} />} onClick={() => onDesfazer(f)}>
                                Desfazer
                            </Button>
                        )}
                        <Button
                            size="xs"
                            variant="ghost"
                            rightIcon={<Icon as={aberto ? FaChevronUp : FaChevronDown} />}
                            onClick={() => setAberto(!aberto)}
                            aria-expanded={aberto}
                        >
                            {f.itens.length} item(ns)
                        </Button>
                    </HStack>
                </Flex>
            </Flex>

            <Collapse in={aberto} animateOpacity>
                <VStack align="stretch" spacing={0} divider={<Divider />} borderTopWidth="1px" borderTopColor="gray.100" bg="gray.50">
                    {f.itens.map((i) => (
                        <Flex key={i.id} px={{ base: 3, md: 4 }} py={2.5} gap={3} align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} opacity={i.desfeito ? 0.6 : 1}>
                            <Box flex="1" minW={0}>
                                <HStack spacing={2} flexWrap="wrap">
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">#{i.venda_id}</Text>
                                    {i.id_origem && <Text fontSize="xs" color="gray.500">{i.id_origem}</Text>}
                                    {i.tipo === 'compensacao' && <Badge colorScheme="purple" textTransform="none" borderRadius="full" px={2} fontSize="10px">Compensação</Badge>}
                                    {i.desfeito && <Badge colorScheme="red" textTransform="none" borderRadius="full" px={2} fontSize="10px">Desfeito</Badge>}
                                </HStack>
                                <Text fontSize="xs" color="gray.500" noOfLines={1}>{i.cliente || ''}</Text>
                                {i.desfeito && i.motivo_desfazer && (
                                    <Text fontSize="xs" color="red.600">Desfeito por {i.desfeito_por || 'Sistema'}: {i.motivo_desfazer}</Text>
                                )}
                            </Box>
                            <Wrap spacing={2} align="center" justify={{ base: 'flex-start', md: 'flex-end' }}>
                                {i.site_status !== 'nao_aplica' && (
                                    <WrapItem>
                                        <Tooltip label={i.site_erro || ''} isDisabled={!i.site_erro} hasArrow fontSize="xs">
                                            <Box>
                                                <StatusComissao site situacao={i.site_status} sufixo={i.site_acao === 'desfazer' ? '(desfazer)' : undefined} />
                                            </Box>
                                        </Tooltip>
                                    </WrapItem>
                                )}
                                <WrapItem>
                                    <Text fontSize="sm" fontWeight="bold" color={i.valor_centavos < 0 ? 'red.600' : 'gray.800'} minW="88px" textAlign="right">
                                        {formatarCentavos(i.valor_centavos)}
                                    </Text>
                                </WrapItem>
                                {podeGerenciar && !i.desfeito && f.itens.filter((x) => !x.desfeito).length > 1 && (
                                    <WrapItem>
                                        <Button size="xs" variant="ghost" colorScheme="red" onClick={() => onDesfazerItem(f, i)}>
                                            Desfazer item
                                        </Button>
                                    </WrapItem>
                                )}
                            </Wrap>
                        </Flex>
                    ))}
                </VStack>
            </Collapse>
        </Box>
    );
};

export default CartaoFechamento;
